import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Text, Spinner, Card } from '@geist-ui/core';
import AuthService from '../../services/auth.service';
import { useAuth } from '../../context/AuthContext';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);

  useEffect(() => {
    const processCallback = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const token = params.get('token');
        const errorParam = params.get('error');
        const redirectParam = params.get('redirect');
        
        if (errorParam) {
          setStatus('error');
          setError(errorParam === 'auth_failed' 
            ? 'Failed to authenticate with Patreon' 
            : errorParam);
          return;
        }
        
        if (!token) {
          setStatus('error');
          setError('Authentication token is missing');
          return;
        }
        
        sessionStorage.setItem('authToken', token);
        
        const userData = await AuthService.getCurrentUser();
        
        if (userData.error) {
          setStatus('error');
          setError(userData.message || 'Failed to get user data');
          return;
        }
        
        // Update the auth context with user data
        setUser(userData);
        setStatus('success');
        
        if (redirectParam === 'subscription-required') {
          setTimeout(() => {
            navigate('/subscription-required', { replace: true });
          }, 1500);
          return;
        }
        
        const redirectTo = location.state?.from || '/bookshelf';
        
        // Short delay to show success message
        setTimeout(() => {
          navigate(redirectTo, { replace: true });
        }, 1500);
      } catch (err) {
        console.error('Error processing authentication callback:', err);
        setStatus('error');
        setError('An unexpected error occurred');
      }
    };
    
    processCallback();
  }, [location, navigate, setUser]);
  
  // Render different UI based on status
  const renderContent = () => {
    switch (status) {
      case 'processing':
        return (
          <>
            <Spinner size="large" />
            <Text h4 style={{ marginTop: '1rem' }}>Processing your login...</Text>
            <Text p type="secondary">Please wait while we verify your Patreon status</Text>
          </>
        );
      case 'success':
        return (
          <>
            <div className="success-icon">✓</div>
            <Text h4 style={{ marginTop: '1rem' }}>Login Successful!</Text>
            <Text p type="secondary">Redirecting you to the app...</Text>
          </>
        );
      case 'error':
        return (
          <>
            <div className="error-icon">✗</div>
            <Text h4 style={{ marginTop: '1rem' }}>Login Failed</Text>
            <Text p type="error">{error || 'An unknown error occurred'}</Text>
            <Text p style={{ marginTop: '1rem' }}>
              <a href="/" style={{ color: '#0070f3', textDecoration: 'none' }}>
                Return to Home
              </a>
            </Text>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="auth-callback-container" style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      backgroundColor: '#f7f7f7'
    }}>
      <Card shadow style={{ 
        padding: '2rem', 
        textAlign: 'center',
        maxWidth: '400px',
        width: '90%'
      }}>
        <div style={{ marginBottom: '1rem' }}>
          <img 
            src="https://pub-3cbdf0fe4d9c40f19241eb1a5a52005c.r2.dev/om-assets/chest.webp" 
            alt="Chester icon" 
            style={{ width: '80px', height: '80px' }}
          />
        </div>
        {renderContent()}
      </Card>

      <style jsx>{`
        .success-icon {
          color: #0070f3;
          font-size: 3rem;
          font-weight: bold;
        }
        .error-icon {
          color: #e00;
          font-size: 3rem;
          font-weight: bold;
        }
      `}</style>
    </div>
  );
};

export default AuthCallback; 